/* ==========================================================================
   BAYRUE — Judge.me "Cards" review widget: count-based "Show more reviews"
   --------------------------------------------------------------------------
   The Cards section theme has no "reviews per page" setting. Judge.me's own
   control is HEIGHT based: useMasonryLayout clamps the grid to 550/800/1050px
   (small/medium/tall) and renders a fade + "Show more" button. This replaces
   that with a COUNT based control: N reviews, +N per click.

   Notes on the widget we're driving:
   * It is a Vue app mounted into #judgeme_product_reviews. We never mutate
     anything inside its vnode tree except adding a <style> rule and toggling
     classes on the mount element, which Vue does not manage.
   * Their "Show more" button also FETCHES the next page from the API
     (15 reviews per page). We hide it visually but click it programmatically
     when we run past the reviews already loaded — otherwise reviews 16+ would
     never be reachable.
   * The list is re-rendered when sort or a filter changes, which resets the
     widget to page 1. We detect that and reset our own counter.

   Companion file: assets/bayrue-judgeme-grid.css
   Config comes from snippets/bayrue-judgeme-grid.liquid via window.BAYRUE_JM_GRID.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.BAYRUE_JM_GRID || {};
  var INITIAL    = parseInt(CFG.initial, 10) || 4;
  var STEP       = parseInt(CFG.step, 10) || 4;
  var BP         = parseInt(CFG.breakpoint, 10) || 767;
  var ON_DESKTOP = CFG.desktop !== false;
  var LABEL      = CFG.label || 'SHOW MORE REVIEWS';
  var LABEL_BUSY = CFG.labelBusy || 'LOADING...';

  var ROOT_SEL  = '#judgeme_product_reviews';
  var CONTAINER = '.jdgm-masonry-grid__container';
  var ITEM      = '.jdgm-masonry-grid__masonry-item';
  var THEIR_BTN = '.jdgm-masonry-grid__btn';

  var mq = window.matchMedia('(max-width: ' + BP + 'px)');

  function boot(root) {
    var container = root.querySelector(CONTAINER);
    if (!container) return false;

    var shown = INITIAL;
    var fetching = false;
    var resetPending = false;
    var lastSig = '';
    var lastCount = -1;
    var wrap = null;
    var btn = null;
    var heightTimer = null;

    var styleEl = document.createElement('style');
    styleEl.setAttribute('data-bayrue-jm', 'gate');
    document.head.appendChild(styleEl);

    function items() {
      return Array.prototype.slice.call(container.querySelectorAll(ITEM));
    }
    function theirBtn() {
      var b = root.querySelector(THEIR_BTN);
      return b && !b.disabled ? b : null;
    }
    // Judge.me only renders its footer while more pages exist, so its presence
    // is the most reliable "there are more reviews on the server" signal.
    function hasMorePages() {
      return !!root.querySelector(THEIR_BTN);
    }
    function gateOn() {
      return mq.matches || ON_DESKTOP;
    }
    function signature() {
      var list = items();
      return list.length + '|' + list.slice(0, 3).map(function (el) {
        return (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 60);
      }).join('~');
    }

    function ensureButton() {
      if (wrap && wrap.isConnected) return;
      wrap = document.createElement('div');
      wrap.className = 'bay-jm-more';
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bay-jm-more__btn';
      btn.textContent = LABEL;
      btn.addEventListener('click', onMore);
      wrap.appendChild(btn);
      root.appendChild(wrap);
    }

    function setBusy(on) {
      if (!btn) return;
      btn.disabled = !!on;
      btn.textContent = on ? LABEL_BUSY : LABEL;
      btn.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    function onMore() {
      var total = items().length;
      shown += STEP;
      // Ran past what's loaded — pull the next page through Judge.me's own button.
      if (shown > total && theirBtn()) {
        fetching = true;
        setBusy(true);
        theirBtn().click();
        // Safety net in case the request fails and no mutation ever lands.
        window.setTimeout(function () {
          if (fetching) { fetching = false; setBusy(false); apply(); }
        }, 6000);
      }
      apply();
    }

    /* Correct the container height on desktop: useMasonryLayout adds
       `offsetHeight + gap` per child, and a display:none child contributes
       0 + 24px, so every hidden card would leave 24px of dead space. */
    function fixHeight() {
      var list = items();
      var hidden = list.length - shown;
      if (mq.matches || !gateOn() || hidden <= 0) {
        root.classList.remove('bay-jm-clipped');
        root.style.removeProperty('--bay-jm-h');
        return;
      }
      var top = container.getBoundingClientRect().top;
      var max = 0;
      for (var i = 0; i < Math.min(shown, list.length); i++) {
        var r = list[i].getBoundingClientRect();
        if (!r.height) continue;
        max = Math.max(max, r.bottom - top);
      }
      if (max > 0) {
        root.style.setProperty('--bay-jm-h', Math.ceil(max) + 'px');
        root.classList.add('bay-jm-clipped');
      }
    }

    /* Deliberately setTimeout, not requestAnimationFrame: rAF is suspended
       while the document is hidden, so a PDP opened in a background tab would
       never get its height corrected. Timers keep running. */
    function scheduleFixHeight() {
      if (heightTimer) window.clearTimeout(heightTimer);
      heightTimer = window.setTimeout(function () {
        heightTimer = null;
        fixHeight();
        // Their masonry animates over 300ms; settle again afterwards.
        window.setTimeout(fixHeight, 340);
      }, 0);
    }

    function apply() {
      if (!gateOn()) {
        // Hand the widget back to Judge.me untouched.
        styleEl.textContent = '';
        root.classList.remove('bay-jm-ready');
        root.classList.remove('bay-jm-clipped');
        root.style.removeProperty('--bay-jm-h');
        if (wrap) wrap.style.display = 'none';
        return;
      }

      root.classList.add('bay-jm-ready');
      ensureButton();

      var list = items();
      if (shown < INITIAL) shown = INITIAL;

      styleEl.textContent = shown < list.length
        ? ROOT_SEL + '.bay-jm-ready ' + ITEM + ':nth-child(n+' + (shown + 1) + '){display:none !important}'
        : '';

      var more = shown < list.length || hasMorePages();
      wrap.style.display = more ? '' : 'none';
      if (!fetching) setBusy(false);

      scheduleFixHeight();
    }

    /* ---- observers -------------------------------------------------------- */

    var mo = new MutationObserver(function () {
      var list = items();
      var sig = signature();
      var grew = list.length > lastCount;

      if (fetching && grew) { fetching = false; setBusy(false); }

      // Sort/filter re-render: the list shrinks, or its head changes while we
      // weren't the ones asking for more.
      var headChanged = !!lastSig && sig.split('|')[1] !== lastSig.split('|')[1];
      if (resetPending || list.length < lastCount || (headChanged && !grew)) {
        shown = INITIAL;
        resetPending = false;
      }

      lastSig = sig;
      lastCount = list.length;
      apply();
    });
    mo.observe(container, { childList: true, subtree: true, characterData: true });

    // Sort dropdown / rating filters live in the widget header.
    var header = root.querySelector('.jm-review-widget-header, .jm-review-widget__header');
    if (header) {
      ['click', 'change'].forEach(function (evt) {
        header.addEventListener(evt, function () { resetPending = true; }, true);
      });
    }

    // Cards grow as their images decode — re-measure.
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(scheduleFixHeight);
      ro.observe(container);
    }
    container.addEventListener('load', scheduleFixHeight, true);

    var onMq = function () { shown = INITIAL; apply(); };
    if (mq.addEventListener) mq.addEventListener('change', onMq);
    else if (mq.addListener) mq.addListener(onMq);

    lastSig = signature();
    lastCount = items().length;
    apply();
    return true;
  }

  /* ---- wait for the Vue widget to mount ---------------------------------- */
  function tryBoot() {
    var root = document.querySelector(ROOT_SEL);
    if (!root) return false;
    return boot(root);
  }

  function start() {
    if (tryBoot()) return;
    var tries = 0;
    var watcher = new MutationObserver(function () {
      if (tryBoot() || ++tries > 400) watcher.disconnect();
    });
    watcher.observe(document.documentElement, { childList: true, subtree: true });
    // Judge.me can mount late on slow connections; stop watching after 60s.
    window.setTimeout(function () { watcher.disconnect(); }, 60000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
